import { commendaAiAttachmentDownloadUrl } from '@commenda/api/commenda-ai/api';
import type {
  CommendaAiGeneratedFileMetadata,
  CommendaAiSavedFile,
} from '@commenda/api/commenda-ai/types';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import type { FileUIPart } from 'ai';
import { useState } from 'react';
import { AttachmentCard } from './AttachmentCard';
import type { PreviewFile } from './AttachmentPreviewPanel';
import { ChatAttachmentHelper } from './ChatAttachmentHelper';
import { useCommendaAiHost } from './CommendaAiHostProvider';
import { GeneratedFileHelper } from './generated-file-helper';
import { SaveToDocumentsModal } from './SaveToDocumentsModal';
import { useCommendaAiWorkspace } from './useCommendaAiWorkspace';

interface GeneratedFileAttachmentCardProps {
  conversationId: string;
  messageId: string;
  part: FileUIPart;
  metadata: CommendaAiGeneratedFileMetadata;
  isStreaming: boolean;
  onPreviewFile: (file: PreviewFile) => void;
}

export function GeneratedFileAttachmentCard({
  conversationId,
  messageId,
  part,
  metadata,
  isStreaming,
  onPreviewFile,
}: GeneratedFileAttachmentCardProps) {
  const { companyId } = useCommendaAiWorkspace();
  const { navigate } = useCommendaAiHost();
  const [localSaved, setLocalSaved] = useState<CommendaAiSavedFile>();
  const savedFile = localSaved ?? metadata.saved;
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const fileName = ChatAttachmentHelper.getDisplayName(part.filename, part.url);
  const downloadUrl = commendaAiAttachmentDownloadUrl(part.url, {
    conversationId,
    messageId,
  });

  const handleManage = () => {
    if (!savedFile) return;
    navigate(GeneratedFileHelper.getDocumentsPath(savedFile));
  };

  const handleSaved = (saved: CommendaAiSavedFile) => {
    setLocalSaved(saved);
    setIsSaveModalOpen(false);
  };

  return (
    <>
      <AttachmentCard
        fileName={fileName}
        mimeType={part.mediaType}
        fileRef={part.url}
        downloadUrl={downloadUrl}
        onPreview={() =>
          onPreviewFile({
            fileName,
            mimeType: part.mediaType,
            fileRef: part.url,
            downloadUrl,
          })
        }
        saveConfig={
          companyId !== null &&
          !isStreaming &&
          isFeatureLaunched(FeatureLaunch.CHAT_SAVE_GENERATED_FILES)
            ? {
                isSaved: savedFile !== undefined,
                onSave: () => setIsSaveModalOpen(true),
                onManage: handleManage,
              }
            : undefined
        }
      />
      {isSaveModalOpen && companyId !== null && (
        <SaveToDocumentsModal
          isOpen
          companyId={companyId}
          fileRef={{ conversationId, messageId, fileUrl: part.url }}
          fileName={fileName}
          suggestion={metadata.suggestion ?? {}}
          onClose={() => setIsSaveModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
